import { InfoBar } from "@components/common/info-bar";
import { AddPlanActions } from "@components/plans-table/add-plan-actions";
import { InviteMemberToWorkspace } from "@pages/workspace-profile/invite-member-to-workspace";
import { CompanyProfileTab } from "@router/route-params";
import { useAppParams } from "@router/router-helper";
import { Box, Stack } from "@mui-wrapper";
import { usePermission } from "@hooks/use-permission";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { WorkspaceEvents } from "@utils/track-event/track-event-list";
import { BaseCompanyProps, CompanyTypeKind } from "@custom-types/base-types";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { updateCompany } from "@store/workspace/workspace-thunks";
import { useErrorHandler } from "@components/error-boundary/error-handling-context";
import { WorkspaceTypeDropdown } from "@pages/workspace-profile/workspace-type-dropdown";
import { companyStateSelector } from "@store/workspace/workspace-slice";
import {
  Chip,
  NO_TRANSLATE_CLASS,
  TextVariant,
  Tooltip,
  useToast,
} from "@flat-ui";
import { TextWithTooltip } from "@components/common/text-with-tooltip";

/**
 * @param tab the open tab of the workspace page
 * @param company the workspace of the page
 * @returns what the open tab offers to do, which is shown at the end of the bar, if anything
 */
function getTabAction(
  tab: CompanyProfileTab | undefined,
  company: BaseCompanyProps["company"]
): React.ReactNode {
  switch (tab) {
    case CompanyProfileTab.plans: {
      return <AddPlanActions subjectType="COMPANY" subjectId={company.id} />;
    }
    case CompanyProfileTab.hardwareLicenses: {
      return <AddPlanActions subjectType="DEVICE" subjectId={company.id} />;
    }
    case CompanyProfileTab.members: {
      return <InviteMemberToWorkspace company={company} />;
    }
    default: {
      return undefined;
    }
  }
}

/**
 * Display company information in the header of company profile page
 */
export function WorkspaceProfileHeader({
  company,
}: BaseCompanyProps): React.JSX.Element {
  const { trackEvent } = useTrackEvent();
  const dispatch = useAppDispatch();
  const { handleErrorWithToast } = useErrorHandler();
  const { openToast } = useToast();
  const { companyProfileTab } = useAppParams();
  const { isHbToSxgMigrated } = useAppSelector(companyStateSelector);
  const { isAllowed: isAllowedToChangeType, deniedReason } = usePermission(
    "workspace.changeType"
  );

  async function onChangeWorkspaceType(
    workspaceType: CompanyTypeKind
  ): Promise<void> {
    trackEvent({
      name: WorkspaceEvents.editWorkspaceDetails,
      props: {
        isNameChanged: false,
        type: workspaceType,
      },
    });

    try {
      await dispatch(
        updateCompany({
          companyId: company.id,
          payload: { type: workspaceType },
        })
      ).unwrap();
      openToast({
        title: "Workspace type updated successfully!",
      });
    } catch (error) {
      handleErrorWithToast({
        id: `edit-workspace-type-${Date.now().toString()}`,
        title: "Failed to update workspace type",
        error,
      });
    }
  }

  const typeDropdown = (
    <WorkspaceTypeDropdown
      selectedType={company.type ?? "unspecified"}
      onTypeSelect={onChangeWorkspaceType}
      variant="underlined"
      isFullWidth={false}
      // Wide enough for the longest type, so the bar does not shift when another one is picked
      width="15rem"
      // The info bar's label is the one above it, so no margin is needed in between
      sx={{ mt: 0 }}
      shouldShowLabel={false}
      isDisabled={!isAllowedToChangeType}
    />
  );

  return (
    <InfoBar
      shouldReserveActionSpace
      action={getTabAction(companyProfileTab, company)}
      items={[
        {
          label: "Workspace",
          content: (
            <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
              <Box
                className={NO_TRANSLATE_CLASS}
                sx={{ minWidth: 0, overflow: "hidden" }}
              >
                <TextWithTooltip
                  text={company.name}
                  textVariant={TextVariant.heading16}
                />
              </Box>
              {isHbToSxgMigrated && (
                <Chip
                  label="Migrated to Sphere XG"
                  color="primary"
                  textTransform="none"
                  tooltip={
                    "This workspace is migrated from HoloBuilder to Sphere XG, " +
                    "meaning that all users in the workspace will use Sphere XG when they open any link pointing to " +
                    "the workspace or any specific project. Users can still use the legacy UI if needed."
                  }
                />
              )}
            </Stack>
          ),
        },
        {
          label: "Workspace Type",
          content: isAllowedToChangeType ? (
            typeDropdown
          ) : (
            // The wrapper receives the hover events, because a disabled dropdown doesn't emit them.
            // It is inline-block so it is only as wide as the dropdown, which is where the tooltip anchors.
            <Tooltip title={deniedReason}>
              <Box
                component="span"
                tabIndex={0}
                sx={{ display: "inline-block" }}
              >
                {typeDropdown}
              </Box>
            </Tooltip>
          ),
        },
      ]}
    />
  );
}
